import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { HandHeart, Star } from 'lucide-react';
import { postsApi, mediaUrl } from '../services/postsApi';
import { useAuth } from '../contexts/AuthContext';

/**
 * Horizontal "Testimonies" highlight reel of answered-prayer posts, pinned at
 * the top of the Community feed. Tapping a testimony opens a lightbox.
 */
const TestimoniesReel = ({ language = 'en' }) => {
  const es = language === 'es';
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [featuring, setFeaturing] = useState(false);

  const feature = async () => {
    if (!active) return;
    setFeaturing(true);
    try {
      await postsApi.feature(active.id);
      toast.success(es ? '¡Destacado como Testimonio de la semana!' : 'Featured as Testimony of the Week!');
      window.dispatchEvent(new CustomEvent('lumera:featured-changed'));
      setActive(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'Error' : 'Something went wrong'));
    } finally { setFeaturing(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await postsApi.testimonies();
        if (!cancelled) setItems(list);
      } catch { if (!cancelled) setItems([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (items.length === 0) return null;

  return (
    <div data-testid="testimonies-reel">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-amber-600 mb-2">
        <HandHeart className="w-4 h-4" /> {es ? 'Testimonios' : 'Testimonies'}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
        {items.map((t) => {
          const initials = (t.author_name || '?').slice(0, 2).toUpperCase();
          return (
            <button
              key={t.id}
              onClick={() => setActive(t)}
              className="shrink-0 flex flex-col items-center gap-1 w-[68px]"
              data-testid={`testimony-${t.id}`}
            >
              <span className="w-16 h-16 rounded-full p-[2.5px] bg-gradient-to-tr from-amber-400 via-rose-400 to-fuchsia-500 block">
                <img
                  src={mediaUrl(t.image_url)}
                  alt=""
                  className="w-full h-full rounded-full object-cover border-2 border-background"
                  onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                />
              </span>
              <span className="text-[11px] text-muted-foreground truncate w-full text-center">{t.author_name}</span>
            </button>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) setActive(null); }}>
        <DialogContent className="max-w-md p-3" data-testid="testimony-lightbox">
          <DialogTitle className="flex items-center gap-2 text-base">
            <HandHeart className="w-5 h-5 text-amber-500" />
            {es ? 'Oración respondida' : 'Answered Prayer'}
          </DialogTitle>
          <DialogDescription className="sr-only">{es ? 'Testimonio de oración respondida' : 'Answered prayer testimony'}</DialogDescription>
          {active && (
            <div className="space-y-3">
              <div className="flex items-center gap-2.5">
                <Avatar className="w-9 h-9">
                  {active.author_picture ? <AvatarImage src={active.author_picture} /> : null}
                  <AvatarFallback className="text-xs bg-primary/15">{(active.author_name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <p className="text-sm font-semibold">{active.author_name}</p>
              </div>
              <img src={mediaUrl(active.image_url)} alt={active.caption || ''} className="w-full rounded-xl bg-muted" />
              {active.caption && <p className="text-sm">{active.caption}</p>}
              {isSuper && (
                <Button onClick={feature} disabled={featuring} className="w-full bg-amber-500 hover:bg-amber-600" data-testid="feature-testimony-btn">
                  <Star className="w-4 h-4 mr-2" /> {es ? 'Destacar esta semana' : 'Feature as Testimony of the Week'}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestimoniesReel;
